import { execAdmin } from '@src/apis/reware/utils';
import { requirePartner } from '@src/providers/dataProvider/shared';

import type { CatalogTemplateRow, WizardState } from './types';

interface CatalogFacet {
  tag: string;
  name: string;
}

interface CatalogItemsResponse {
  catalogItems: {
    count: number;
    page: number;
    pageSize: number;
    rows: Array<{
      parentSku: string | null;
      sku_subcode: string | null;
      color: string | null;
      colorCode: string | null;
      sizeCode: string | null;
      catalogFacets: CatalogFacet[] | null;
      partner_category: string | null;
      price: number | null;
      yerdlePrice: number | null;
      title: string | null;
    }> | null;
  } | null;
}

interface CatalogItemsVariables {
  partnerId: number;
  page: number;
  pageSize: number;
  facets?: { department?: string, category?: string } | null;
  parentSku?: string | null;
}

const QUERY = `
  query getCatalogItemsForTemplate(
    $partnerId: Int!
    $page: Int
    $pageSize: Int
    $facets: FacetFiltersInputTypeAdmin
    $parentSku: String
  ) {
    catalogItems(
      partnerId: $partnerId
      page: $page
      pageSize: $pageSize
      facets: $facets
      parentSku: $parentSku
      availability: "ALL"
    ) {
      count
      page
      pageSize
      rows {
        parentSku
        sku_subcode
        color
        colorCode
        sizeCode
        catalogFacets { tag name }
        partner_category
        price
        yerdlePrice
        title
      }
    }
  }
`;

const PAGE_SIZE = 500;

type RawRow = NonNullable<NonNullable<CatalogItemsResponse['catalogItems']>['rows']>[number];

function facetValue(facets: CatalogFacet[] | null, tag: string): string | null {
  if (!facets) return null;
  return facets.find(f => f.tag === tag)?.name ?? null;
}

const toRow = (raw: RawRow): CatalogTemplateRow => ({
  parentSku: raw.parentSku,
  skuSubcode: raw.sku_subcode,
  color: raw.color,
  colorCode: raw.colorCode,
  sizeCode: raw.sizeCode,
  department: facetValue(raw.catalogFacets, 'department'),
  partnerCategory: raw.partner_category ?? facetValue(raw.catalogFacets, 'category'),
  price: raw.price,
  yerdlePrice: raw.yerdlePrice,
  title: raw.title,
});

// Limits how many parent-SKU lookups run concurrently against admin_graphql.
const SKU_FETCH_CONCURRENCY = 5;

/**
 * Fetches all pages of catalogItems for a fixed set of base variables
 * (everything except page/pageSize).
 */
async function fetchAllPages(
  baseVars: Omit<CatalogItemsVariables, 'page' | 'pageSize'>,
): Promise<CatalogTemplateRow[]> {
  const firstVars: CatalogItemsVariables = { ...baseVars, page: 1, pageSize: PAGE_SIZE };
  const firstData = await execAdmin<CatalogItemsVariables, CatalogItemsResponse>(
    QUERY,
    { variables: firstVars },
  );

  const catalogItems = firstData.catalogItems;
  if (!catalogItems?.rows) {
    return [];
  }

  const allRows: CatalogTemplateRow[] = catalogItems.rows
    .filter((r): r is NonNullable<typeof r> => r != null)
    .map(toRow);

  // Fetch remaining pages — use the actual pageSize the server returned,
  // which may be smaller than the requested PAGE_SIZE.
  const actualPageSize = catalogItems.pageSize ?? PAGE_SIZE;
  const totalPages = Math.ceil((catalogItems.count ?? 0) / actualPageSize);
  for (let page = 2; page <= totalPages; page += 1) {
    const data = await execAdmin<CatalogItemsVariables, CatalogItemsResponse>(
      QUERY,
      { variables: { ...baseVars, page, pageSize: PAGE_SIZE } },
    );
    const rows = data.catalogItems?.rows;
    if (rows) {
      allRows.push(
        ...rows.filter((r): r is NonNullable<typeof r> => r != null).map(toRow),
      );
    }
  }

  return allRows;
}

async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const current = nextIndex;
      nextIndex += 1;
      results[current] = await fn(items[current]);
    }
  }

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

/**
 * Fetches catalog items for template generation.
 *
 * Scope filtering:
 * - departments: single department/category filters server-side via the
 *   facets parameter; multiple selections fetch all and filter client-side
 *   (the catalogItems API only accepts single string values for facets)
 * - parentSkus: each SKU is looked up individually via the server-side
 *   parentSku filter, so results always reflect exactly the requested SKUs
 *   regardless of how large the partner's overall catalog is
 */
export const fetchCatalogItemsForTemplate = async (
  state: WizardState,
): Promise<CatalogTemplateRow[]> => {
  const partner = requirePartner();
  const partnerId = partner.id;

  if (state.scopeMode === 'parentSkus') {
    const skus = getParentSkuList(state.parentSkuInput);
    const rowsPerSku = await mapWithConcurrency(
      skus,
      SKU_FETCH_CONCURRENCY,
      sku => fetchAllPages({ partnerId, parentSku: sku }),
    );
    return rowsPerSku.flat();
  }

  const baseVars: Omit<CatalogItemsVariables, 'page' | 'pageSize'> = { partnerId };
  if (state.scopeMode === 'departments') {
    const facets: { department?: string, category?: string } = {};
    if (state.selectedDepartments.length === 1) {
      facets.department = state.selectedDepartments[0];
    }
    if (state.selectedCategories.length === 1 && state.selectedDepartments.length <= 1) {
      facets.category = state.selectedCategories[0];
    }
    if (facets.department || facets.category) {
      baseVars.facets = facets;
    }
  }

  const allRows = await fetchAllPages(baseVars);
  return applyClientSideFilters(allRows, state);
};

function getParentSkuList(input: string): string[] {
  return input
    .split(/[,\n]+/)
    .map(s => s.trim())
    .filter(Boolean);
}

function applyClientSideFilters(rows: CatalogTemplateRow[], state: WizardState): CatalogTemplateRow[] {
  if (state.scopeMode === 'departments') {
    const depts = new Set(state.selectedDepartments);
    const cats = new Set(state.selectedCategories);
    return rows.filter((row) => {
      if (depts.size > 1 && !depts.has(row.department ?? '')) return false;
      if (cats.size > 1 && !cats.has(row.partnerCategory ?? '')) return false;
      return true;
    });
  }

  return rows;
}
